'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

import { buildVisiblePageNumbers } from '@/utils/pagination'

interface PageNumberPaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
  className?: string
}

export function PageNumberPagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  className,
}: PageNumberPaginationProps) {
  const safeTotalPages = Math.max(totalPages || 1, 1)
  const safeCurrentPage = Math.min(Math.max(currentPage || 1, 1), safeTotalPages)
  const pages = buildVisiblePageNumbers(safeCurrentPage, safeTotalPages)

  if (safeTotalPages <= 1) {
    return null
  }

  const handlePageChange = (page: number) => {
    if (disabled || page < 1 || page > safeTotalPages || page === safeCurrentPage) {
      return
    }

    onPageChange(page)
  }

  const prevDisabled = safeCurrentPage <= 1 || disabled
  const nextDisabled = safeCurrentPage >= safeTotalPages || disabled

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            disabled={prevDisabled}
            onClick={event => {
              event.preventDefault()
              handlePageChange(safeCurrentPage - 1)
            }}
            className={prevDisabled ? undefined : 'cursor-pointer'}
          />
        </PaginationItem>

        {pages.map((page, index) =>
          page === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                href="#"
                isActive={page === safeCurrentPage}
                disabled={disabled}
                onClick={event => {
                  event.preventDefault()
                  handlePageChange(page)
                }}
                aria-label={`Page ${page}`}
                aria-current={page === safeCurrentPage ? 'page' : undefined}
                className={cn(disabled ? undefined : 'cursor-pointer')}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationNext
            href="#"
            disabled={nextDisabled}
            onClick={event => {
              event.preventDefault()
              handlePageChange(safeCurrentPage + 1)
            }}
            className={nextDisabled ? undefined : 'cursor-pointer'}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
